<script setup>
import { ref, watch } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import Dropdown from 'primevue/dropdown';
import moment from 'moment';
import InputNumber from 'primevue/inputnumber';
import axios from 'axios';

const props = defineProps({
    dealers: Array,
    lots: Array,
    selectedLotId: Number,
    selectedDealerId: Number
});

let eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const form = useForm({
    lot_id: null,
    dealer_id: null,
    discount: 0,
    discount_reason: null,
    extra_price: 0,
    extra_price_reason: null,
    price: null,
    fee: 0
});

const currentDealer = ref(null);
const currentLot = ref(null);
//when dealer_id on form changes, set dealer as current dealer
watch(() => form.dealer_id, (value) => {
    if (value) {
        const dealer = props.dealers.find(dealer => dealer.id === value);
        currentDealer.value = dealer;
    }
});
//when lot_id on form changes, set lot as current lot
watch(() => form.lot_id, (value) => {
    if (value) {
        const lot = props.lots.find(lot => lot.id === value);
        currentLot.value = lot;
        form.price = lot.sold_price;
    }
});

///when price on form changes, get the fee
watch(() => form.price, (value) => {
    if (value) {
        getFee(value);
    }
});

function submit() {
    form.post('/portal/facturen/aanmaken', {
        onSuccess: () => {
            form.reset();
        }
    });
}

const fee = ref(0);

function getFee(amount) {
  //do axios call to backend to get the fee for the given amount
  axios.post('/portal/getFee', {amount:amount}).then(response => {
    fee.value = response.data.fee;
    form.fee = response.data.fee;
  });
}

//set lot id if selectedLotId is set
if (props.selectedLotId) {
    form.lot_id = parseInt(props.selectedLotId);
}
if (props.selectedDealerId) {
    form.dealer_id = parseInt(props.selectedDealerId);
}
</script>

<template>
    <Head title="Factuur aanmaken | Mijnautosnelweg" />
    <form class="px-4 sm:px-6 py-6 space-y-12" @submit.prevent="submit">

        <div class="grid grid-cols-1 gap-2.5 border-b border-gray-900/10 pb-12 md:grid-cols-2">
            <div class="flex flex-col gap-6">
                <div>
                    <h2 class="text-base font-semibold leading-7 text-gray-900">Klant informatie</h2>
                </div>
                <div class="flex flex-col gap-2 text-sm font-base">
                    <div class="flex flex-row gap-12 items-center">
                        <p class="w-48">Kavelnummer</p>
                        <div class="w-96">
                            <Dropdown v-model="form.lot_id"  :options="props.lots" filter optionLabel="id" optionValue="id"
                                placeholder="Selecteer Kavel" class="dropdown-style" />
                        </div>
                    </div>
                    <div class="flex flex-row gap-12 items-center">
                        <p class="w-48">Bedrijfsnaam</p>
                        <div class="w-96">
                            <Dropdown v-model="form.dealer_id" :options="props.dealers" filter optionLabel="company_name" optionValue="id"
                                placeholder="Selecteer dealer" class="dropdown-style" />
                        </div>
                    </div>
                    <div class="flex flex-row gap-12 items-center" v-if="currentDealer">
                        <p class="w-48">T.a.v.</p>
                        <p class="w-96 text-gray-500 py-1"> {{currentDealer ? currentDealer.full_name : 'Kies dealer'}} </p>
                    </div>
                    <div class="flex flex-row gap-12 items-center" v-if="currentDealer">
                        <p class="w-48">Klant adres</p>
                        <p class="w-96 text-gray-500 py-1"> {{currentDealer ? currentDealer.full_address : 'Kies dealer'}} </p>
                    </div>
                    <div class="flex flex-row gap-12 items-center" v-if="currentDealer">
                        <p class="w-48">Factuur adres</p>
                        <p class="w-96 text-gray-500 py-1"> {{currentDealer ? currentDealer.full_invoice_address : 'Kies dealer'}} </p>
                    </div>
                    <div class="flex flex-row gap-12 items-center" v-if="currentDealer">
                        <p class="w-48">KvK nummer</p>
                        <p class="w-96 text-gray-500 py-1"> {{currentDealer ? currentDealer.chamber_of_commerce_number : 'Kies dealer'}} </p>
                    </div>
                    <div class="flex flex-row gap-12 items-center" v-if="currentDealer">
                        <p class="w-48">BTW nummer</p>
                        <p class="w-96 text-gray-500 py-1"> {{ currentDealer ? currentDealer.vat_number : 'Kies dealer' }} </p>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-6 border-b border-gray-900/10 pb-12">
            <div>
                <h2 class="text-base font-semibold leading-7 text-gray-900">Factuur informatie</h2>
            </div>
            <div class="flex flex-col lg:grid lg:grid-cols-4 gap-4 lg:gap-8">
                <div class="flex flex-row lg:flex-col gap-3.5">
                    <div class="flex flex-col gap-0.5 h-full w-full">
                        <p>Datum</p>
                        <p class="text-gray-500 py-1"> {{ moment().format('DD-MM-YYYY') }} </p>
                    </div>
                    <div class="flex flex-col gap-0.5 h-full w-full">
                        <p>Vervaldatum</p>
                        <p class="text-gray-500 py-1"> {{ moment().add(1, 'M').format('DD-MM-YYYY') }} </p>
                    </div>
                </div>
                <div class="flex flex-row lg:flex-col gap-3.5">
                    <div class="flex flex-col gap-0.5 w-full">
                        <p>Korting</p>
                        <InputNumber v-model="form.discount" inputId="currency-netherlands" mode="currency" currency="EUR" locale="nl-NL" />
                    </div>
                    <div class="flex flex-col gap-0.5 w-full">
                        <p>Toevoeging</p>
                        <InputNumber v-model="form.extra_price" inputId="currency-netherlands" mode="currency" currency="EUR" locale="nl-NL" />
                    </div>
                </div>
                <div class="flex flex-row lg:flex-col gap-3.5 col-span-2">
                    <div class="flex flex-col gap-0.5 w-full">
                    <p>Toelichting</p>
                    <textarea id="about" name="about" rows="5" class="textarea-style h-10"
                        placeholder="Reden korting" v-model="form.discount_reason" />
                    </div>
                    <div class="flex flex-col gap-0.5 w-full">
                    <p>Toelichting</p>
                    <textarea id="about" name="about" rows="5" class="textarea-style h-10"
                        placeholder="Reden toevoeging" v-model="form.extra_price_reason" />
                    </div>
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-6 border-b border-gray-900/10 pb-12" v-if="currentLot">
            <div>
                <h2 class="text-base font-semibold leading-7 text-gray-900">Kavelinformatie</h2>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-2.5 divide-y md:divide-y-0">
                <div class="flex flex-col gap-0.5">
                    <p>Kavelnummer</p>
                    <p class="text-gray-500 py-1">#{{ currentLot ? currentLot.id : 'Kies kavel' }}</p>
                </div>
                <div class="flex flex-col gap-0.5">
                    <p>Kavelomschrijving</p>
                    <p class="text-gray-500 py-1" v-if="currentLot"> {{ currentLot.make }} {{ currentLot.model }} {{ currentLot.construction_year }}</p>
                    <p class="text-gray-500 py-1" v-else> Kies kavel </p>
                </div>
                <div class="flex flex-col gap-0.5">
                    <p>Kenteken</p>
                    <p class="text-gray-500 py-1"> {{currentLot ? currentLot.license_plate ? currentLot.license_plate : 'n.v.t.' : 'Kies kavel' }} </p>
                </div>
                <div class="flex flex-col gap-0.5">
                    <p>Verkoopbedrag (incl. fee)</p>
                    <InputNumber v-model="form.price" inputId="currency-netherlands" mode="currency" currency="EUR" locale="nl-NL" />
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-6 border-b border-gray-900/10 pb-12" v-if="currentLot && currentDealer && form.price">
            <div>
                <h2 class="text-base font-semibold leading-7 text-gray-900">Prijsberekening</h2>
            </div>
            <div class="flex flex-col md:flex-row gap-y-8 md:items-center justify-between">
                    <div class="flex flex-col gap-0.5">
                        <p class="text-gray-500 text-sm"> Fee bedrag kavel </p>
                        <p class="text-gray-900 text-lg font-semibold">{{ form.price ? eurFormatter.format(fee) : 'Voer bedrag in' }} </p>
                        <p class="text-gray-500 text-xs h-4" v-if="form.price"> {{  eurFormatter.format(fee / 100 * 21) }} </p>
                    </div>
                    <font-awesome-icon :icon="['fal', 'plus']" class="hidden md:block w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                    <div class="relative flex flex-col gap-0.5 border-b md:border-none pb-2 md:pb-0">
                        <p class="text-gray-500 text-sm"> Toevoeging </p>
                        <p class="text-gray-900 text-lg font-semibold"> {{ eurFormatter.format(form.extra_price) }} </p>
                        <p class="text-gray-500 text-xs h-4"> {{ eurFormatter.format((form.extra_price) / 100 * 21) }} </p>
                        <font-awesome-icon :icon="['fal', 'plus']" class="absolute block md:hidden bottom-3 right-0 w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                    </div>
                    <font-awesome-icon :icon="['fal', 'equals']" class="hidden md:block w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                    <div class="flex flex-col gap-0.5">
                        <p class="text-gray-500 text-sm"> Subtotaal </p>
                        <p class="text-gray-900 text-lg font-semibold" v-if="form.price"> {{ eurFormatter.format(fee + form.extra_price)}} </p>
                        <p class="text-gray-500 text-xs h-4" v-if="form.price"> {{ eurFormatter.format((fee + form.extra_price) / 100 * 21) }} </p>
                    </div>
                    <font-awesome-icon :icon="['fal', 'minus']" class="hidden md:block w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                <div class="relative flex flex-col gap-0.5 border-b md:border-none pb-2 md:pb-0">
                    <p class="text-gray-500 text-sm"> Korting </p>
                    <p class="text-gray-900 text-lg font-semibold"> {{ eurFormatter.format(form.discount) }} </p>
                    <p class="text-gray-500 text-xs h-4"> {{ eurFormatter.format(form.discount / 100 * 21) }} </p>
                    <font-awesome-icon :icon="['fal', 'minus']" class="absolute block md:hidden bottom-3 right-0 w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                </div>
                <font-awesome-icon :icon="['fal', 'equals']" class="hidden md:block w-5 h-5 text-gray-500 shrink-0" aria-hidden="true" />
                <div class="flex flex-col gap-0.5">
                    <p class="text-gray-500 text-sm"> Totaal</p>
                    <p class="text-gray-900 text-lg font-semibold" v-if="form.price"> {{ eurFormatter.format((fee + form.extra_price) - form.discount) }} </p>
                    <p class="text-gray-500 text-xs h-4"> {{ eurFormatter.format(((fee + form.extra_price) - form.discount) / 100 * 21) }} </p>
                </div>
            </div>
        </div>
        <div class="mt-6 flex items-center justify-end gap-x-6">
            <Link href="/portal/rollen" type="button" class="text-sm font-semibold leading-6 text-gray-900">Annuleren
            </Link>
            <button type="submit"
                class="rounded-md bg-brightorange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brightorange">Verzenden</button>
        </div>
    </form>
</template>